import type { GuideChapter } from '../types'
import { n } from '../types'

export const stock: GuideChapter = {
  id: 'lager',
  title: 'Lager, henvendelser og beskeder',
  icon: '📦',
  intro: `Varer ind og ud af bilen, henvendelser fra kunderne, akutte udkald og beskeder til holdet.`,
  sections: [
    {
      id: 'stock-scan',
      title: 'Scan ind og ud',
      icon: '📲',
      role: 'Medarbejder',
      module: 'stock',
      where: `"Scan ind og ud" i menuen, eller "Scan flere" på sagen`,
      intro: `Scan varerne i en kurv og registrér dem på én gang: ind i bilen, brugt på en sag, købt hjem eller taget ud.`,
      sketch: {
        device: 'phone',
        title: 'Scan ind og ud',
        nav: true,
        blocks: [
          {
            t: 'card',
            title: 'Hvad skal der ske?',
            children: [
              { t: 'btns', items: [{ text: 'Ind i bilen' }, { text: 'Brugt på sag', primary: true, n: 1 }, { text: 'Købt hjem' }, { text: 'Taget ud' }] },
              { t: 'field', label: 'Fra', value: 'Bil 3 (min bil)', n: 2 },
              { t: 'field', label: 'Sag', value: 'Tavleudskiftning, opgang B' }
            ]
          },
          { t: 'photo', label: 'Kamera - scan varerne', n: 3 },
          {
            t: 'card',
            title: 'Kurven (2)',
            children: [
              { t: 'row', text: 'Kabel NOIKLX 3G1,5', meta: '− 40 m +' },
              { t: 'row', text: 'HPFI-afbryder', meta: '− 1 stk +' },
              { t: 'btn', text: 'Registrér 2 varer', primary: true, n: 4 }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Materialer brugt på en sag',
          steps: [
            n(1, `Tryk "Brugt på sag".`),
            n(2, `Tjek "Fra" - din egen bil er valgt på forhånd - og vælg sagen.`),
            n(3, `Scan varerne. Samme vare to gange tæller 2.`),
            n(4, `Ret antal med − og +, og tryk "Registrér".`)
          ]
        },
        { title: 'Fyld bilen', steps: [`Tryk "Ind i bilen", scan, og tryk "Registrér".`] }
      ],
      tips: [
        `Kurven gemmes samlet - alt eller intet - og tømmes først, når serveren har svaret.`,
        `Er der ikke valgt en bil på forhånd, står du ikke som ansvarlig for en bil. Spørg projektlederen.`
      ]
    },
    {
      id: 'stock-van-orders',
      title: 'Bestil varer til bilen',
      icon: '🛻',
      role: 'Medarbejder',
      module: 'stock_van_orders',
      where: `"Varer til bilen" i menuen - eller "+" → "Varer til bilen"`,
      intro: `Bestil det, du mangler i bilen. Lageret pakker det, og du får besked, når det kan hentes. Varerne flyttes først til bilen, når du trykker "Hentet".`,
      sketch: {
        device: 'phone',
        title: 'Varer til bilen',
        nav: true,
        blocks: [
          {
            t: 'card',
            title: 'Ny bestilling',
            children: [
              { t: 'field', label: 'Til bilen', value: 'Bil 3', n: 1 },
              { t: 'btns', items: [{ text: '🔄 Fyld op', n: 2 }, { text: '📷 Scan' }] },
              { t: 'field', label: 'Søg i kartoteket', value: 'PVT 3G1,5', n: 3 },
              { t: 'row', text: 'PVT 3G1,5', meta: '50 m' },
              { t: 'row', text: 'Wago 221-413 · fri tekst', meta: '20', n: 4 },
              { t: 'btn', text: 'Send bestillingen (2)', primary: true, n: 5 }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Bestil',
          steps: [
            n(1, `Din bil er valgt på forhånd. Står der ingen, er du ikke sat på en bil - spørg kontoret.`),
            n(2, `"Fyld op" lægger alt, der er under bilens minimum, i bestillingen. Det, der allerede er bestilt, trækkes fra.`),
            n(3, `Søg eller scan resten af varerne fra kartoteket.`),
            n(4, `Står varen ikke i kartoteket, så skriv den som fri tekst.`),
            n(5, `Ret antallene, skriv evt. en besked til lageret, og tryk "Send bestillingen".`)
          ]
        },
        {
          title: 'Hent varerne',
          steps: [
            `Du får en besked, når bestillingen er pakket - med lagerets besked om, hvor den står.`,
            `Åbn bestillingen, og tryk "Hentet - flyt varerne til bilen", når du har dem.`,
            `Var noget ikke på lager, står det som rest. Tryk "Bestil resten" for at bestille det igen.`
          ]
        }
      ],
      tips: [
        `Du kan annullere din bestilling, indtil den er pakket. Derefter skal du bede lageret om det.`,
        `Kun kartotekets varer flyttes i beholdningen - fri tekst er bare en besked til lageret.`
      ]
    },
    {
      id: 'stock-van-orders-pack',
      title: 'Pak varer til bilerne',
      icon: '📦',
      role: 'Medarbejder',
      module: 'stock_van_orders',
      where: `"Varer til bilen" i menuen - åbn en bestilling`,
      intro: `Arbejdsgruppen med fluebenet "Pakker varer til bilerne" pakker bestillingerne og får besked om nye. Har ingen gruppe fluebenet, er det kontoret. Kontoret og administratorerne kan altid pakke.`,
      flows: [
        {
          title: 'Pak en bestilling',
          steps: [
            `Åbn bestillingen under "Åbne bestillinger".`,
            `Vælg hvor du pakker fra (lageret eller en kasse).`,
            `Skriv hvor mange der er pakket af hver vare. 0 = ikke på lager - så står det som rest.`,
            `Skriv evt. hvor bestillingen står, og tryk "Pakket - giv montøren besked".`
          ]
        },
        {
          title: 'Sæt en arbejdsgruppe til at pakke',
          steps: [
            `Gå til Intranet → Arbejdsgrupper, og ret gruppen.`,
            `Sæt fluebenet "Pakker varer til bilerne", og gem.`
          ]
        }
      ],
      tips: [
        `Du kan rette det pakkede, indtil montøren har trykket "Hentet".`,
        `Er intet på lager, så annullér bestillingen - montøren får besked.`
      ]
    },
    {
      id: 'stock-items',
      title: 'Varer og lagre',
      icon: '🚚',
      role: 'Medarbejder',
      module: 'stock',
      where: `"Varer" og "Lagre og biler" i menuen`,
      intro: `Slå en vare op ved at scanne æsken, se hvad der ligger i bilen, flyt varer mellem lagre, og tæl op.`,
      flows: [
        { title: 'Slå en vare op', steps: [`Åbn "Varer", og scan æsken eller mærkatet.`] },
        {
          title: 'Tæl op i bilen',
          steps: [`Åbn "Lagre og biler" → din bil.`, `Tryk "Tæl op" ved varen, skriv det faktiske antal, og tryk "Gem".`]
        },
        {
          title: 'Flyt fra lageret',
          steps: [`Åbn det sted, varen skal hen, og tryk "Læg noget ind".`, `Vælg vare og antal, vælg "Fra [sted]", og tryk "Læg ind".`]
        }
      ],
      tips: [`Et negativt tal står med rødt: der er scannet mere ud end ind. Tæl op.`]
    },
    {
      id: 'stock-manage',
      title: 'Opret varer, biler og lagre',
      icon: '🏭',
      role: 'Projektleder',
      module: 'stock',
      where: `"Varer" → "Ny vare", "Lagre og biler" → "Nyt sted"`,
      intro: `Projektledere opretter varer og steder og sætter en ansvarlig på bilerne.`,
      flows: [
        { title: 'Ny vare', steps: [`Scan en ukendt stregkode - formularen åbner med koden udfyldt - eller tryk "Ny vare".`, `Udfyld, og tryk "Gem".`] },
        {
          title: 'Ny bil',
          steps: [`Tryk "Nyt sted", vælg "Bil", og udfyld navn og nummerplade.`, `Vælg "Hvem har bilen", og tryk "Gem".`]
        }
      ],
      tips: [
        `En vare, der udgår: fjern fluebenet "I brug" frem for at slette.`,
        `Et lager eller en bil kan kun slettes af en administrator, og kun når der ikke ligger noget. Sæt det ud af brug i stedet.`
      ]
    },
    {
      id: 'price-import',
      title: 'Prisfiler fra grossisten',
      icon: '🏷️',
      role: 'Administrator',
      module: 'stock',
      where: `Administration → "Prisfiler"`,
      intro: `Læs en prisfil ind og se, hvad den ændrer, før priserne sættes.`,
      flows: [
        {
          title: 'Læs en prisfil ind',
          steps: [
            `Vælg opsætning og fil, og tryk "Vis hvad filen gør".`,
            `Gennemgå "Store spring".`,
            `Tryk "Sæt N priser" - eller "Fortryd".`
          ]
        },
        { title: 'Fortryd en kørsel', steps: [`Under "Tidligere kørsler": tryk "Rul tilbage".`] }
      ],
      tips: [`Store spring over 50 % tyder på en glemt rabat eller et forkert decimaltegn.`]
    },
    {
      id: 'support',
      title: 'Henvendelser',
      icon: '📨',
      role: 'Medarbejder',
      module: 'support',
      where: `"Henvendelser" i menuen`,
      intro: `En kunde ringer, eller noget i systemet driller: skriv det ind som en henvendelse, så det ikke bliver glemt.`,
      flows: [
        {
          title: 'Opret fra et opkald',
          steps: [
            `Tryk "Ny henvendelse", og vælg "📞 Kundehenvendelse".`,
            `Skriv emne og beskrivelse, vælg kunde, og skriv hvem der ringede.`,
            `Tryk "Opret".`
          ]
        },
        { title: 'Svar', steps: [`Åbn henvendelsen, skriv i "Skriv", og tryk "Send svar". Har kunden en mail, sendes svaret som mail.`] }
      ],
      tips: [`Som medarbejder ser du de henvendelser, du selv har meldt eller er sat på.`]
    },
    {
      id: 'support-manage',
      title: 'Fordel henvendelser og gør dem til sager',
      icon: '📬',
      role: 'Projektleder',
      module: 'support',
      where: `"Henvendelser"`,
      intro: `Projektledere ser alle henvendelser, sætter en ansvarlig på og kan gøre en henvendelse til en sag.`,
      flows: [
        { title: 'Fordel', steps: [`Åbn henvendelsen, vælg "Ansvarlig", og skift "Tilstand". Det gemmes med det samme.`] },
        {
          title: 'Gør til en sag',
          steps: [`Vælg kunden, og skriv ordrenummeret.`, `Tryk "Gør til en sag". Henvendelsen bliver stående med et link til sagen.`]
        }
      ],
      tips: [`Sæt flueben i "Intern note", hvis svaret ikke må gå ud af huset.`]
    },
    {
      id: 'support-mail',
      title: 'Mail ind i indbakken',
      icon: '📥',
      role: 'Administrator',
      module: 'support',
      where: `Administration → "Support-mail"`,
      intro: `Mails til en fast postkasse bliver automatisk til henvendelser.`,
      flows: [
        {
          title: 'Sæt det op',
          steps: [
            `Microsoft 365 skal være sat op først.`,
            `Udfyld postkasse og mappe, vælg interval, og tryk "Gem".`,
            `Prøv med "Hent nu", og sæt så flueben i "Læs postkassen automatisk".`
          ]
        }
      ],
      tips: [`Det skal være en rigtig postkasse - et alias kan ikke læses.`]
    },
    {
      id: 'field-messages-receive',
      title: 'Når du får et akut udkald',
      icon: '🚨',
      role: 'Medarbejder',
      module: 'field_messages',
      where: `Banneret øverst i appen`,
      intro: `Et udkald eller en besked fra kontoret står som et banner øverst på alle sider, indtil du har kvitteret.`,
      sketch: {
        device: 'phone',
        title: 'Mit overblik',
        nav: true,
        blocks: [
          {
            t: 'banner',
            tone: 'danger',
            text: '🚨 AKUT UDKALD · Strømsvigt i teknikrum · Industrivej 12'
          },
          { t: 'btns', items: [{ text: 'Jeg har set den', primary: true, n: 1 }, { text: 'Jeg er på vej', n: 2 }, { text: 'Afslut', n: 3 }] },
          { t: 'field', label: 'Hvad blev der gjort?', n: 4 }
        ]
      },
      flows: [
        {
          title: 'Kvittér',
          steps: [
            n(1, `Tryk "Jeg har set den" - så ved kontoret, at du har den.`),
            n(2, `Tryk "Jeg er på vej", når du kører.`),
            n(3, `Tryk "Afslut", når det er klaret.`),
            n(4, `Skriv, hvad der blev gjort, og tryk "Gem".`)
          ]
        }
      ],
      tips: [`Systemet erstatter ikke et opkald. Haster det, så ring.`]
    },
    {
      id: 'field-messages-send',
      title: 'Send et akut udkald',
      icon: '📢',
      role: 'Projektleder',
      module: 'field_messages',
      where: `"Akut og udkald"`,
      intro: `Send én mand ud på et akut job, eller send en besked til dem, der er på en sag i dag - og følg, hvornår de har set den.`,
      flows: [
        {
          title: 'Akut udkald',
          steps: [`Tryk "🚨 Akut udkald".`, `Skriv hvad det drejer sig om, vælg én mand, og udfyld adresse og kontakt.`, `Tryk "Send udkald".`]
        },
        {
          title: 'Besked til et job',
          steps: [`Tryk "🔔 Besked til et job", og vælg sagen. Dem, der er på sagen i dag, står øverst.`, `Vælg modtagere, og tryk "Send besked".`]
        }
      ],
      tips: [`Et udkald går til én person. Skal flere ud, så send flere. Tryk "Afblæs", hvis det ikke længere er nødvendigt.`]
    },
    {
      id: 'messages',
      title: 'Send SMS eller mail til holdet',
      icon: '📣',
      role: 'Projektleder',
      where: `"Beskeder"`,
      intro: `Send en besked til udvalgte medarbejdere med det samme - på SMS, mail eller begge.`,
      flows: [
        {
          title: 'Send en besked',
          steps: [
            `Vælg modtagere. Står der "intet nummer", kan personen ikke få SMS.`,
            `Vælg "Send som SMS" og/eller "Send som mail", og skriv beskeden.`,
            `Tryk "Send til N", og læs, hvem der ikke fik den.`
          ]
        }
      ],
      tips: [`Beskeden går afsted med det samme, og en SMS koster penge.`]
    },
    {
      id: 'messages-rules',
      title: 'Automatiske beskeder og morgenbesked',
      icon: '⚙️',
      role: 'Administrator',
      where: `"Beskeder" → "Åbningstid for beskeder", "Automatiske beskeder" og "Morgenbesked med dagens plan"`,
      intro: `Bestem, hvilke af systemets beskeder der også skal gå ud som SMS eller mail, hvornår de må sendes, og om folk skal have dagens plan om morgenen.`,
      flows: [
        { title: 'Automatiske beskeder', steps: [`Sæt flueben pr. besked og kanal (SMS/mail), og tryk "Gem".`] },
        {
          title: 'Åbningstid (Superadmin)',
          steps: [
            `Under "Åbningstid for beskeder" står tiderne pr. ugedag - som udgangspunkt mandag-torsdag 07.00-15.15 og fredag 07.00-14.45.`,
            `Ret tiderne, eller sæt flueben i "Lukket" for en dag, og tryk "Gem åbningstid".`,
            `Uden for åbningstiden venter beskederne i kø - push på telefonen, SMS og mail - og sendes, når der åbner. Beskeden kan stadig ses inde i appen med det samme.`,
            `Sæt flueben i "Vigtig" ved de beskeder, der skal ud med det samme - også om aftenen og i weekenden.`
          ]
        },
        {
          title: 'Morgenbesked',
          steps: [`Tryk "Send nu" for at se, hvad folk får.`, `Sæt flueben i "Send morgenbeskeden automatisk", vælg klokkeslæt, og tryk "Gem".`]
        }
      ],
      tips: [
        `SMS og mail står som standard til fra.`,
        `En besked, der allerede er læst i appen, sendes ikke som SMS eller mail bagefter. Kun Superadmin kan ændre åbningstiden og "Vigtig".`,
        `Den, der intet har i kalenderen, får ingen morgenbesked.`
      ]
    },
    {
      id: 'deadline-alerts',
      title: 'Deadline-varsler',
      icon: '⏰',
      role: 'Administrator',
      where: `Administration → "Deadline-varsler"`,
      intro: `Varsler om deadlines og kalibreringer, der nærmer sig. De lander under Notifikationer.`,
      flows: [
        { title: 'Sæt op', steps: [`Sæt flueben, vælg antal dage og klokkeslæt, og tryk "Gem".`, `Tryk "Send nu" for at køre dem med det samme.`] }
      ],
      tips: [`Det samme varsel sendes kun én gang. Flyttes deadline, varsles der igen.`]
    },
    {
      id: 'subscriptions',
      title: 'Abonnementer og fakturering',
      icon: '💳',
      role: 'Administrator',
      module: 'subscriptions',
      where: `Administration → "Abonnementer"`,
      intro: `Faste aftaler, kunden betaler for. Systemet danner faktureringsgrundlaget - selve fakturaen laves i økonomisystemet.`,
      flows: [
        { title: 'Nyt abonnement', steps: [`Tryk "Nyt abonnement", udfyld kunde, tekst, beløb og interval, og tryk "Gem".`] },
        { title: 'Fakturér', steps: [`Tryk "Dan grundlag nu", og gennemgå "Klar til fakturering".`] }
      ],
      tips: [`Alle beløb er uden moms, og der faktureres forud. Et faktureret abonnement kan ikke slettes - sæt en slutdato.`]
    }
  ]
}
